import { wcisDomain } from "@js/nwcc-domains.js";
import { getQueryArgs } from "@js/nwcc-navbar.js";
import { loadMainNwcc, setArgDetailsData } from "@js/nwcc-template.js";
import { renderGrowl } from "@js/nwcc-growl.js";
import { resizePlot } from "@js/nwcc-plotting.js";
import { extendAddDays } from "@js/nwcc-extensions.js";
import { splitCamel } from "@js/nwcc-utils.js";
import "@js/nwcc-inline-alert.js";

import { makePlot, awdbPlotId } from "./home-plotting.js";
import { makeTable, awdbTableId } from "./home-table.js";
import {
    pickerToApiDateFormat,
    getDefaultDateConvention,
    writeClipboardText,
    convertDateToRelative,
    networkDefaultReports,
} from "./home-utils.js";
import {
    addFilterListeners,
    zoomToSelectedStation,
    populateFilters,
    getFilteredStations,
    loadFilterMap,
    updateStations,
    clearMetaDropDowns,
    updateFilterMap,
    stationSelectionChange,
} from "./home-filter-modal.js";
import * as getData from "../model/get-data.js";

window.onresize = resizePlot;

const defaultElements = [
    "WTEQ",
    "PREC",
    "SNWD",
    "TOBS",
    "TAVG",
    "TMAX",
    "TMIN",
    "PRCP",
    "SMS",
    "STO",
    "WSPDV",
    "WSPDX",
    "WSPDN",
    "WSPD",
    "WDIR",
    "WDIRV",
];
export const defaultNetworkFilter = "SNTL";

export function getPageArgs() {
    return [
        {
            argName: "stationTriplet",
            argDesc: "AWDB Station Triplet (unique station ID), takes the form stationID:stateCode:networkCode.",
            argExample: "1099:UT:SNTL",
            elemId: "select--station",
        },
        {
            argName: "element",
            argDesc:
                "Station element code or special report name of the requested data, if the selected site does not have this element the default will be returned.",
            argExample: "temperature report",
            argDefault: "varies based on network",
            elemId: "select--element",
        },
        {
            argName: "allElements",
            argDesc:
                "Allows selection of all station elements, otherwise only curated/common list is available in element dropdown.",
            argExample: "false",
            argOptions: ["true", "false"],
            argDefault: "false",
        },
        {
            argName: "duration",
            argDesc:
                "Sets the duration of the requested data, if the selected station element does not have this duration the default will be returned. Note, special reports take no duration.",
            argExample: "daily",
            argDefault: "daily",
            elemId: "select--duration",
        },
        {
            argName: "beginDate",
            argDesc:
                "Sets the begin date of the requested data, at midnight of the selected day. Integer days back (-∞ through 0 are also valid).",
            argExample: "2020-10-01",
            argDefault: "varies based on duration",
            elemId: "date-picker--begin",
        },
        {
            argName: "endDate",
            argDesc:
                "Sets the end date of the requested data, through the entirety of the selected day. Integer days back (-∞ through 1 are also valid).",
            argExample: "2021-09-30",
            argDefault: "varies based on duration",
            elemId: "date-picker--end",
        },
        {
            argName: "returnFlags",
            argDesc: "Toggle the return of quality control flags with data",
            argExample: "false",
            argDefault: "false",
            argOptions: ["true", "false"],
        },
        {
            argName: "stateFilter",
            argDesc: "2-character state abbreviation, sets the selected state in the map filters.",
            argExample: "ut",
            elemId: "select--state",
        },
        {
            argName: "regionFilter",
            argDesc: "2-digit Hydrologic Unit Code (HUC), sets the selected region in the map filters.",
            argExample: "16",
            elemId: "select--region",
        },
        {
            argName: "networkFilters",
            argDesc:
                "Comma delineated list of AWDB network codes, sets the selected networks filter in the map's filters.",
            argExample: "sntl,sntlt",
            argOptions: getData.getValidNetworks().map((network) => network.toLowerCase()),
            argDefault: defaultNetworkFilter.toLowerCase(),
            elemId: "cb--networks",
        },
        {
            argName: "boundingBox",
            argDesc:
                "Sets the extents of latitudes and longitudes to return sites for, comma delineated list of the form: NW lat,long , SE lat,long.",
            argExample: "39.5,-114,37.5,-109",
        },
        {
            argName: "format",
            argDesc: "Create a plot or table, minimum of stationTriplet must be supplied for success.",
            argExample: "plot",
            argOptions: ["plot", "table"],
        },
        {
            argName: "showMap",
            argDesc: "Show site filter map on page load.",
            argExample: "false",
            argOptions: ["true", "false"],
            argDefault: "false",
        },
        {
            argName: "returnSuspectData",
            argDesc: "Returns data regardless of qcFlag",
            argExample: "false",
            argOptions: ["true", "false"],
            argDefault: "false",
            argInternal: true,
        },
        {
            argName: "returnOriginalValues",
            argDesc: "Returns orignal data in addition to the current, possibly edited, data.",
            argExample: "false",
            argOptions: ["true", "false"],
            argDefault: "false",
            argInternal: true,
        },
        {
            argName: "ordinal",
            argDesc: "Returns higher ordinal data",
            argExample: "1",
            argDefault: "1",
            argInternal: true,
        },
        {
            argName: "dco",
            argDesc: "Returns only stations associated with a specific data collection office",
            argExample: "ut",
            argOptions: ["AK", "CO", "GC", "ID", "MT", "OR", "UN", "UT", "WC"],
            argDefault: "false",
            argInternal: true,
        },
    ];
}

window.onload = windowOnLoad;

export async function windowOnLoad() {
    setArgDetailsData(getPageArgs());
    extendAddDays();
    await setupPage();
    loadMainNwcc();
}

export async function setupPage() {
    const welcome = document.querySelector("nwcc-inline-alert");
    welcome.setBody(getWelcomeHTML());
    await Promise.all([getData.fetchOverlays(), getData.updateReferenceData()]);
    await Promise.all([populateNetworks(), parseNetworks(), installDatePickers(), getData.updateMeta()]);
    await Promise.all([
        addListeners(),
        populateFilters(),
        parseFilters(),
        loadFilterMap(),
        addReportsInfo(),
        updateStations(),
        parseStation(),
    ]);
    await setBookmarkTextBox();
    showMapOnLoad();
}

export function getWelcomeHTML() {
    return /*html*/ `
        This page hosts applications for analyzing snow, water, and climate data from the National Water and Climate Center (NWCC). Use any of the links in the navigation bar above to access data, maps, and reports. You can also visit the webpages for
        <a href="https://www.nrcs.usda.gov/programs-initiatives/sswsf-snow-survey-and-water-supply-forecasting-program/national-water-and">NWCC</a>
        and the
        <a href="https://www.nrcs.usda.gov/programs-initiatives/sswsf-snow-survey-and-water-supply-forecasting-program">Snow Survey and Water Supply Forecasting Program</a>
        to learn more, or the
        <a href="https://www.nrcs.usda.gov/contact/national-center-contacts/national-water-and-climate-center-contact-information">Contact Us</a>
        page for assistance.`;
}
export async function addListeners() {
    addMakeListeners();
    addFilterListeners();
    addNetworkListeners();
    addStationListener();
    addElementListener();
    addDurationListener();
    addModalListeners("reports-info");
    addModalListeners("filter", zoomToSelectedStation);
    addBookmarkListeners();
}

export async function getBookmarkLink(plotId = awdbPlotId, tableId = awdbTableId) {
    let useArgs = [];
    const urlArgs = getQueryArgs();
    const pageArgs = getPageArgs();
    const isInternalBookmark = urlArgs.internal || false;
    pageArgs.forEach((arg) => {
        if (!arg.argInternal || isInternalBookmark) {
            useArgs.push(arg);
        }
    });
    if (isInternalBookmark) {
        useArgs.push({ argName: "internal" });
    }
    const elementSelect = document.getElementById("select--element");
    const selectedElementLabel = elementSelect?.selectedOptions[0]
        ? elementSelect.selectedOptions[0].innerHTML.toUpperCase()
        : "";
    let isSpecialReport = false;
    if (Object.keys(getData.specialReports).includes(selectedElementLabel)) {
        isSpecialReport = true;
    }
    const loc = window.location;
    let url = `${loc.protocol}//${loc.host}${loc.path ? loc.path : ""}/`;
    let firstArg = true;
    useArgs.forEach((arg) => {
        const elemId = arg.elemId;
        if (elemId) {
            const elemType = elemId.split("--")[0];
            const argName = arg.argName;
            let skipArg = false;
            if (isSpecialReport && ["beginDate", "endDate", "duration"].includes(argName)) {
                skipArg = true;
            }
            let argVal = false;
            if (elemType === "select") {
                const argElem = document.getElementById(elemId);
                argVal = argElem?.value;
                if (argName === "element" && argVal.includes(",")) {
                    argVal = argElem[argElem.selectedIndex].text.toLowerCase();
                }
            }
            if (elemType === "cb") {
                const checkedBoxes = [];
                const listItems = document.querySelectorAll(`input.fsa-checkbox[name="${elemId}"]`);
                listItems.forEach(function (cb) {
                    const cbVal = cb.id?.split("--")[1];
                    if (cb.checked && !checkedBoxes.includes(cbVal)) {
                        checkedBoxes.push(cbVal);
                    }
                    argVal = checkedBoxes.join(",");
                });
            }
            if (elemType === "date-picker") {
                const dateSelect = document.getElementById(elemId);
                if (dateSelect.value) {
                    const dateStr = dateSelect?.value;
                    if (dateStr) {
                        argVal = pickerToApiDateFormat(dateStr);
                        if (document.getElementById("switch--relative-date")?.checked) {
                            argVal = convertDateToRelative(argVal);
                        }
                    }
                }
            }
            if (argVal && !skipArg) {
                url += `${firstArg ? "?" : "&"}${argName}=${argVal}`;
                firstArg = false;
            }
        } else if (arg.argName != "format" && Object.keys(urlArgs).includes(arg.argName)) {
            url += `${firstArg ? "?" : "&"}${arg.argName}=${urlArgs[arg.argName]}`;
            firstArg = false;
        } else if (arg.argName === "format") {
            const plot = document.getElementById(plotId) ? "plot" : false;
            const table = document.getElementById(tableId) ? "table" : false;
            if (plot || table) {
                url += `${firstArg ? "?" : "&"}${arg.argName}=${plot || table}`;
                firstArg = false;
            }
        }
    });
    history.replaceState({}, "", url);
    return url;
}

export async function addBookmarkListeners() {
    const miscControls = ["btn--start-over", "switch--relative-date"];
    miscControls.forEach((controlId) => {
        const control = document.getElementById(controlId);
        control?.addEventListener("click", async (e) => {
            await setBookmarkTextBox();
        });
    });

    const pageArgs = getPageArgs();
    const pageControls = pageArgs.filter((arg) => arg.elemId).map((arg) => arg.elemId);
    pageControls.forEach((elemId) => {
        const elemType = elemId.split("--")[0];
        if (elemType === "cb") {
            const listItems = document.querySelectorAll(`input[name="${elemId}"]`);
            listItems.forEach(function (control) {
                control.addEventListener("click", async () => {
                    await setBookmarkTextBox();
                });
            });
        } else if (elemType === "date-picker") {
            const datePicker = document.getElementById(elemId);
            datePicker?.addEventListener("change", async () => {
                await setBookmarkTextBox();
            });
            const datePickerCalendar = document.getElementById(`fd-${elemId}`);
            datePickerController.addEvent(datePickerCalendar, "click", async () => {
                setTimeout(async () => {
                    await setBookmarkTextBox();
                });
            });
        } else {
            const control = document.getElementById(elemId);
            control?.addEventListener("change", async () => {
                await setBookmarkTextBox();
            });
        }
    });
    document.getElementById("btn--open-bookmark")?.addEventListener("click", async () => {
        window.open(await getBookmarkLink(), "_blank");
    });
    document.getElementById("btn--copy-bookmark")?.addEventListener("click", async () => {
        copyBookmarkToClipboard();
    });
}

export async function setBookmarkTextBox() {
    const url = await getBookmarkLink();
    document.getElementById("tb--bookmark").value = url;
}

export async function copyBookmarkToClipboard() {
    const success = writeClipboardText(await getBookmarkLink());
    const msg = success
        ? "The bookmark was refreshed and copied to the clipboard."
        : "Your browser policy does not support this action";
    renderGrowl("Copied bookmark to clipboard.", msg, success ? "success" : "error", true);
}

export async function installDatePickers(queryArgs, beginId = "date-picker--begin", endId = "date-picker--end") {
    const today = new Date();
    const tomorrow = today.addDays(1);
    const ids = [beginId, endId];
    const args = queryArgs || getQueryArgs();
    ids.forEach((id) => {
        const pickerType = id.split("--")[1];
        const pickerArg = `${pickerType}Date`;
        const pickerElem = document.getElementById(id);
        const options = { formElements: { [`${id}`]: "%m/%d/%Y" }, statusFormat: "%l, %d%S %F %Y" };
        datePickerController.createDatePicker(options);
        datePickerController.setRangeLow(id, "19000101");
        datePickerController.setRangeHigh(id, pickerType === "begin" ? today : tomorrow);
        datePickerController.show(id);
        datePickerController.hide(id);

        if (Object.keys(args).includes(pickerArg)) {
            const isRelative = !Number.isNaN(Number(args[pickerArg]));
            const pickerDate = Date.parse(args[pickerArg]);
            if (isRelative) {
                const relativeDate = today.addDays(Math.floor(args[pickerArg]));
                const pickerDateStr = new Date(relativeDate).toLocaleDateString("en-US", { timeZone: "PST" });
                pickerElem.value = pickerDateStr;
            } else if (pickerDate !== 0) {
                const pickerDateStr = new Date(pickerDate).toLocaleDateString("en-US", { timeZone: "PST" });
                pickerElem.value = pickerDateStr;
            }
        }
    });
    setDatePickerPlaceholders();
}

export function showMapOnLoad(queryArgs) {
    const args = queryArgs || getQueryArgs();
    const showMap = args?.showMap === true || `${args?.showMap}`.toLowerCase() === "true";
    if (showMap) {
        openModal("modal--filter");
    }
}

export async function addReportsInfo(validSpecialReports = "all") {
    const reportsInfoModal = document.getElementById("modal--reports-info");
    const modalDialog = reportsInfoModal.children[0].children[0];
    const closeBtn = modalDialog.children[0];
    modalDialog.innerHTML = "";
    modalDialog.appendChild(closeBtn);
    const dl = document.createElement("dl");
    Object.keys(getData.specialReports).forEach((report) => {
        if (validSpecialReports === "all" || validSpecialReports.includes(report.toUpperCase())) {
            const dt = document.createElement("dt");
            dt.innerHTML = `${report}`;
            dl.appendChild(dt);
            const elements = document.createElement("dd");
            const elementList = getData.specialReports[report].elements.map((element) => {
                return getData.getRefData().elements[element]?.name?.toUpperCase() || "Unknown";
            });
            elements.innerHTML = `Elements: ${elementList.join(", ")}`;
            dl.appendChild(elements);
            const timeframe = document.createElement("dd");
            const timeframeDesc = getData.specialReports[report].timeframe || `${specialReports[report].daysBack}-DAYS`;
            timeframe.innerHTML = `Timeframe: ${timeframeDesc.toUpperCase()}`;
            dl.appendChild(timeframe);
            const duration = document.createElement("dd");
            duration.innerHTML = `Duration: ${getData.specialReports[report].duration.toUpperCase()}`;
            dl.appendChild(duration);
            const description = document.createElement("dd");
            const descriptionText = getData.specialReports[report].description;
            if (descriptionText) {
                description.innerHTML = descriptionText;
                dl.appendChild(description);
            }
            const divider = document.createElement("div");
            divider.className = "fsa-divider";
            dl.appendChild(divider);
        }
    });
    const dt = document.createElement("dt");
    dt.innerHTML = "INDIVIDUAL ELEMENTS";
    dl.appendChild(dt);
    const description = document.createElement("dd");
    description.innerHTML = "A single element can be chosen for any available duration and timeframe.";
    dl.appendChild(description);
    modalDialog.appendChild(dl);
}

export async function addModalListeners(modalName, onOpen = () => {}, onClose = () => {}) {
    const modalId = modalName.startsWith("modal--") ? modalName : `modal--${modalName}`;
    const openBtnId = `btn-show-${modalId}`;
    const openModalBtn = document.getElementById(openBtnId);
    openModalBtn?.addEventListener("click", () => {
        openModal(modalId, onOpen);
    });

    const closeBtnId = `btn-close-${modalId}`;
    const closeModalBtn = document.getElementById(closeBtnId);
    closeModalBtn?.addEventListener("click", () => {
        closeModal(modalId, onClose);
    });
}

export function openModal(modalId, onOpen = () => {}) {
    const modal = document.getElementById(modalId);
    modal.ariaHidden = false;
    document.body.classList.add("fsa-modal-scroll-fix");
    modal.focus();
    onOpen();
}

export function closeModal(modalId, onClose = () => {}) {
    const modal = document.getElementById(modalId);
    const closeBtnId = `btn-close-${modalId}`;
    const closeModalBtn = document.getElementById(closeBtnId);
    closeModalBtn.blur();
    modal.ariaHidden = true;
    modal.ariaExpanded = false;
    document.body.classList.remove("fsa-modal-scroll-fix");
    onClose();
}

export async function parseNetworks(queryArgs) {
    const args = queryArgs || getQueryArgs();
    const selectedStationTriplet = args?.stationTriplet?.toLowerCase();
    const networkFiltersArg = args?.networkFilters || defaultNetworkFilter;
    if (selectedStationTriplet) {
        const tripletArr = selectedStationTriplet.split(":");
        if (tripletArr.length === 3) {
            const network = `cb--${tripletArr[2].toLowerCase()}`;
            let networkFilters = networkFiltersArg
                .split(",")
                .map((networkFilter) => `cb--${networkFilter.toLowerCase()}`);
            if (!networkFilters.includes(network)) {
                networkFilters.push(network);
            }
            const networksCb = document.querySelectorAll('[name="cb--networks"]');
            networksCb.forEach(function (networkCb) {
                if (networkFilters.includes(networkCb.id)) {
                    networkCb.checked = true;
                } else {
                    networkCb.checked = false;
                }
            });
        }
    } else {
        const networkFilters = networkFiltersArg
            .split(",")
            .map((networkFilter) => `cb--${networkFilter.toLowerCase()}`);
        // TODO: could add some logic here to allow addition of non-default networks, if any networkFilters are not in default you could gather metadata again and populate the newtork cb's again
        const networksCb = document.querySelectorAll('[name="cb--networks"]');
        networksCb.forEach(function (networkCb) {
            if (networkFilters.includes(networkCb.id)) {
                networkCb.checked = true;
            } else {
                networkCb.checked = false;
            }
        });
    }
}

export function addNetworkListeners() {
    const networksCb = document.querySelectorAll('input[name="cb--networks"]');
    networksCb.forEach(function (networkCb) {
        networkCb.addEventListener("click", async () => {
            await networkChange();
        });
    });
}

export async function networkChange() {
    const stateFilter = document.getElementById("select--state");
    const regionFilter = document.getElementById("select--region");
    const stationSelect = document.getElementById("select--station");
    const stateValue = stateFilter.value || "";
    const regionValue = regionFilter.value || "";
    const stationValue = stationSelect.value || "";
    await updateStations();
    await populateFilters();
    stateFilter.value = stateValue;
    regionFilter.value = regionValue;
    if (stationValue) {
        stationSelect.value = stationValue;
        populateElements();
        populateDurations();
    }
    await getBookmarkLink();
    await updateFilterMap();
}

export async function parseStation(queryArgs) {
    const args = queryArgs || getQueryArgs();
    const selectedStationTriplet = args?.stationTriplet?.toUpperCase();
    const meta = getData.getMeta();
    if (selectedStationTriplet) {
        const availableTriplets = meta.map((stationMeta) => stationMeta.stationTriplet.toUpperCase());
        if (availableTriplets.includes(selectedStationTriplet)) {
            document.getElementById("select--station").value = selectedStationTriplet;
            await stationSelectionChange();
            makeRequestedReport();
        }
    }
}

export function makeRequestedReport(queryArgs) {
    const args = queryArgs || getQueryArgs();
    const formatArg = args?.format?.toLowerCase();
    if (formatArg) {
        if (args.format.toLowerCase() === "plot") {
            makePlot();
        }
        if (args.format.toLowerCase() === "table") {
            makeTable();
        }
    }
}

export async function parseFilters(queryArgs) {
    const args = queryArgs || getQueryArgs();
    const selectedStationTriplet = args.stationTriplet?.toUpperCase();
    const stateFilter = args.stateFilter || "";
    const regionFilter = args.regionFilter || "";
    const filteredMeta = getFilteredStations();
    const metaStates = [...new Set(filteredMeta.map((stationMeta) => stationMeta.stateCode.toUpperCase()))];
    const metaRegions = [
        ...new Set(
            filteredMeta.map((stationMeta) => {
                if (stationMeta.huc) {
                    return stationMeta.huc.slice(0, 2);
                }
            }),
        ),
    ];
    const stateFilterSelect = document.getElementById("select--state");
    const regionFilterSelect = document.getElementById("select--region");
    if (selectedStationTriplet) {
        const selectedStationMeta = getData
            .getMeta()
            .filter((stationMeta) => stationMeta.stationTriplet === selectedStationTriplet)[0];
        if (selectedStationMeta) {
            const selectedStationState = selectedStationMeta.stateCode;
            const selectedStationHuc = selectedStationMeta.huc;
            if (metaStates.includes(stateFilter.toUpperCase())) {
                if (stateFilter.toUpperCase() != selectedStationState.toUpperCase()) {
                    stateFilterSelect.value = "";
                } else {
                    stateFilterSelect.value = stateFilter.toUpperCase();
                }
            }
            if (metaRegions.includes(regionFilter)) {
                if (!selectedStationHuc.startsWith(regionFilter)) {
                    regionFilterSelect.value = "";
                } else {
                    regionFilterSelect.value = regionFilter;
                }
            }
            updateStations();
        }
    } else {
        stateFilterSelect.value = stateFilter.toUpperCase();
        regionFilterSelect.value = regionFilter;
    }
}

export function addStationListener() {
    const stationSelect = document.getElementById("select--station");
    stationSelect.addEventListener("change", async () => {
        await stationChange(stationSelect);
    });
    const stationClearBtns = document.querySelectorAll('button[id^="btn-clear--station"]');
    stationClearBtns.forEach((clearBtn) => {
        clearBtn.addEventListener("click", async () => {
            await clearSelectedStation();
        });
    });
}

export async function stationChange(stationSelect) {
    if (stationSelect?.value) {
        await stationSelectionChange();
    } else {
        await getData.clearGlobalSelectedStationData();
        await updateStations();
        updateFilterMap();
    }
}

export async function clearSelectedStation() {
    await getData.clearGlobalSelectedStationData();
    await updateStations();
    updateFilterMap();
    document.getElementById("select--station").value = "";
    document.getElementById("tb--station").value = "";
}

export function addElementListener() {
    document.getElementById("select--element").addEventListener("change", () => {
        populateDurations();
    });
}

export function setDatePickerPlaceholders(queryArgs) {
    const args = queryArgs || getQueryArgs();
    const durationArg = args?.duration;
    const durationSelect = document.getElementById("select--duration");
    const duration = durationSelect?.value || durationArg;
    const beginPlaceholder = duration ? getDefaultDateConvention(duration, "begin") : "mm/dd/yyyy";
    const beginDatePicker = document.getElementById("date-picker--begin");
    if (beginDatePicker) {
        beginDatePicker.placeholder = beginPlaceholder;
    }

    const endPlaceholder = duration ? getDefaultDateConvention(duration, "end") : "mm/dd/yyyy";
    const endDatePicker = document.getElementById("date-picker--end");
    if (endDatePicker) {
        endDatePicker.placeholder = endPlaceholder;
    }
}

export function addDurationListener() {
    document.getElementById("select--duration").addEventListener("change", () => {
        setDatePickerPlaceholders();
    });
}

export function addMakeListeners() {
    document.getElementById("btn--make-table").addEventListener("click", makeTable);
    document.getElementById("btn--make-plot").addEventListener("click", makePlot);
    document.getElementById("btn--start-over").addEventListener("click", async () => {
        await startOverClick();
    });
}

export async function startOverClick() {
    const awdbGrid = document.getElementById("awdb--grid");
    awdbGrid.className = "fsa-hide";
    document.getElementById("date-picker--begin").value = "";
    document.getElementById("date-picker--end").value = "";
    document.getElementById("awdb--output").innerHTML = "";
    await getData.clearGlobalSelectedStationData();
    clearMetaDropDowns();
    await updateStations();
    updateFilterMap();
}

export async function populateNetworks(queryArgs) {
    const args = queryArgs || getQueryArgs();
    const networksLookUp = getData.getRefData().networks;
    const selectedStationTriplet = args.stationTriplet?.toUpperCase();
    if (selectedStationTriplet) {
        const selectedStationNetwork = selectedStationTriplet.split(":")[2].toUpperCase();
        if (
            !getData.getValidNetworks().includes(selectedStationNetwork) &&
            Object.keys(networksLookUp).includes(selectedStationNetwork)
        ) {
            await getData.addValidNetwork(selectedStationNetwork);
        }
    }
    const networkFilters = args.networkFilters?.split(",") || "";
    if (networkFilters) {
        await networkFilters.forEach(async (networkFilter) => {
            if (
                !getData.getValidNetworks().includes(networkFilter.toUpperCase()) &&
                Object.keys(networksLookUp).includes(networkFilter.toUpperCase())
            ) {
                await getData.addValidNetwork(networkFilter);
            }
        });
    }
    const cbListId = "cb-list--networks";
    const networkChoiceField = document.getElementById(cbListId).parentElement;
    const ul = document.createElement("ul");
    ul.className = "fsa-form-list";
    ul.setAttribute("aria-labelledby", cbListId);
    networkChoiceField.appendChild(ul);
    const validNetworks = getData.getValidNetworks();
    validNetworks.forEach((network) => {
        const isChecked = args.networkFilters
            ?.split(",")
            .map((network) => {
                return network.trim().toLowerCase();
            })
            .includes(network.toLowerCase());
        const li = document.createElement("li");
        const span = document.createElement("span");
        const inputId = `cb--${network.toLowerCase()}`;
        const input = document.createElement("input");
        input.className = "fsa-checkbox";
        input.id = inputId;
        input.name = "cb--networks";
        input.type = "checkbox";
        input.checked = isChecked;
        span.appendChild(input);
        li.appendChild(span);
        const label = document.createElement("label");
        label.setAttribute("for", inputId);
        label.innerHTML = networksLookUp[network.toUpperCase()]?.name;
        span.appendChild(label);
        ul.appendChild(li);
    });
    const help = document.createElement("span");
    help.className = "fsa-field__help";
    help.id = "checkbox-list--networks-help";
    help.innerHTML = "Select from available networks";
    networkChoiceField.appendChild(help);
}

export function populateDurations() {
    const elementSelect = document.getElementById("select--element");
    if (elementSelect.value) {
        const durationSelect = document.getElementById("select--duration");
        const durationField = document.getElementById("field--duration");
        const selectedElementLabel = elementSelect.selectedOptions[0].innerHTML.toUpperCase();
        if (Object.keys(getData.specialReports).includes(selectedElementLabel)) {
            durationField?.classList.add("fsa-hide");
            document.getElementById("field--start-date")?.classList.add("fsa-hide");
            document.getElementById("field--end-date")?.classList.add("fsa-hide");
        } else {
            durationField?.classList.remove("fsa-hide");
            document.getElementById("field--start-date")?.classList.remove("fsa-hide");
            document.getElementById("field--end-date")?.classList.remove("fsa-hide");
        }
        const initialDurationSelect = durationSelect.value;
        durationSelect.innerHTML = "";
        const selectedElements = elementSelect.value.split(",");
        const allStationElements = getData.getAllSelectedStationElements();
        const elementMeta = allStationElements.filter((element) => selectedElements.includes(element.elementCode));
        const durations = [...new Set(elementMeta.map((element) => element.durationName))];
        durations.forEach(function (duration) {
            const option = document.createElement("option");
            option.value = duration;
            option.innerHTML = duration.replace("_", " ");
            durationSelect.appendChild(option);
        });
        const args = getQueryArgs();
        const argDuration = args.duration ? args.duration.toUpperCase() : "";
        if (argDuration && durations.includes(argDuration.toUpperCase())) {
            durationSelect.value = argDuration.toUpperCase();
        } else {
            if (durations.includes(initialDurationSelect)) {
                durationSelect.value = initialDurationSelect.toUpperCase();
            } else {
                durationSelect.value = durations.includes("DAILY") ? "DAILY" : durations[0].toUpperCase();
            }
        }
    }
    setDatePickerPlaceholders();
}

export function filterElements(stationElements, defaultOnly = true) {
    const filteredElements = {};
    stationElements.forEach(function (element) {
        if (defaultElements.includes(element.elementCode?.toUpperCase()) || !defaultOnly) {
            filteredElements[element.elementCode?.toUpperCase()] = element.storedUnitCode;
        }
    });
    return filteredElements;
}

export function populateElements(queryArgs) {
    const args = queryArgs || getQueryArgs();
    const elementRefData = getData.getRefData().elements;
    const useAllElements = args.allElements ? args.allElements : false;
    const elementSelect = document.getElementById("select--element");
    elementSelect.innerHTML = "";
    const stationElements = getData.getAllSelectedStationElements();
    const allStationElements = filterElements(stationElements, false);
    if (stationElements.length > 0) {
        const elements =
            Object.keys(allStationElements).length === 0
                ? allStationElements
                : filterElements(stationElements, !useAllElements);
        const defaultGrp = document.createElement("optgroup");
        defaultGrp.label = "SPECIAL REPORTS";
        let hasSpecialReportElements = false;
        const validSpecialReports = [];
        Object.keys(getData.specialReports).forEach(function (reportName) {
            const reportElements = getData.specialReports[reportName].elements;
            if (reportElements.every((reportElement) => Object.keys(allStationElements).includes(reportElement))) {
                hasSpecialReportElements = true;
                const option = document.createElement("option");
                option.value = reportElements.join(",");
                validSpecialReports.push(reportName.toUpperCase());
                option.innerHTML = reportName.toUpperCase();
                defaultGrp.appendChild(option);
            }
        });
        if (hasSpecialReportElements) {
            elementSelect.appendChild(defaultGrp);
        }
        const elementsGrp = document.createElement("optgroup");
        elementsGrp.label = "INDIVIDUAL ELEMENTS";
        elementSelect.appendChild(elementsGrp);
        Object.keys(elements).forEach(function (element) {
            const option = document.createElement("option");
            option.value = element;
            option.innerHTML = `${elementRefData[element].name} (${elements[element]})`.toUpperCase();
            elementsGrp.appendChild(option);
        });
        const elementArg = args.element?.toUpperCase();
        if (elementArg && Object.keys(elements).includes(elementArg.toUpperCase())) {
            elementSelect.value = elementArg.toUpperCase();
        } else if (elementArg && Object.keys(getData.specialReports).includes(elementArg.toUpperCase())) {
            elementSelect.value = getData.specialReports[elementArg.toUpperCase()].elements;
        } else {
            if (hasSpecialReportElements) {
                const selectedStationMeta = getData.getSelectedStationMeta();
                const selectedStationNetwork = selectedStationMeta ? selectedStationMeta.networkCode : false;
                if (selectedStationNetwork) {
                    const defaultReport = networkDefaultReports[selectedStationNetwork];
                    const defaultElementList = getData.specialReports[defaultReport].elements;
                    if (validSpecialReports.includes(defaultReport)) {
                        elementSelect.value = defaultElementList;
                    }
                }
            }
        }
        addReportsInfo(validSpecialReports);
        populateDurations();
    }
}

export function scrollToOutput() {
    document.getElementById("select--station").parentElement.scrollIntoView(true);
}

export function renderInvalidElementGrowl(queryArgs) {
    const args = queryArgs || getQueryArgs();
    const elementSelect = document.getElementById("select--element");
    let elementLabel = /*thml*/ `element=<b>${args.element || ""}</b>`;
    if (args.duration) {
        elementLabel = /*html*/ `${elementLabel}<br>duration=<b>${args.duration}</b>`;
    }
    if (args.ordinal) {
        elementLabel = /*html*/ `${elementLabel}<br>ordinal=<b>${args.ordinal}</b>`;
    }
    const msg = `No elements were returned for this query<br><i>${elementLabel}</i><br> Please verify your query is correct and try again.`;
    renderGrowl("Not a valid element for this station!", msg, "error", true, 10);
    elementSelect.focus();
}

export function renderInvalidStationGrowl() {
    const stationSelect = document.getElementById("select--station");
    const msg =
        "You must select a station before creating a plot, select a station in the drop down or edit the filters to include more stations.";
    renderGrowl("Please select a station first!", msg, "error", true, 10);
    stationSelect.focus();
}

export function attachStationMetaModalButton(attachTo, modalId = "modal--station-meta") {
    const stationMeta = getData.getSelectedStationMeta();
    const duration = document.getElementById("select--duration")?.value.toLowerCase();
    const beginDate =
        document.getElementById("date-picker--begin")?.value || getDefaultDateConvention(duration, "begin");
    const endDate = document.getElementById("date-picker--end")?.value || getDefaultDateConvention(duration, "end");
    const rgDates = `${pickerToApiDateFormat(beginDate)},${pickerToApiDateFormat(endDate)}`;
    const selectedElementsArray = document.getElementById("select--element").value.split(",");
    const rgElements = [];
    selectedElementsArray.forEach((element) => rgElements.push(`${element}::value`));
    const rgLink = `${wcisDomain}/reportGenerator/view/customMultiTimeSeriesGroupByStationReport/${duration}/end_of_period/${stationMeta.stationTriplet}|id=%22%22|name/${rgDates}/${rgElements.join(",")}`;
    const imapLink = `/imap/#&popup=${stationMeta.stationTriplet}&lat=${stationMeta.latitude}&lon=${stationMeta.longitude}&zoom=8.5`;
    const photoLink = `https://www.wcc.nrcs.usda.gov/siteimages/${stationMeta.stationId}.jpg`;
    const sitePhotoDivId = "div--site-photo";
    const stationBeginDate = stationMeta.beginDate.substring(0, 10);

    const modal = document.createElement("div");
    const showModalBtnId = `btn-show-${modalId}`;
    const closeModalBtnId = `btn-close-${modalId}`;
    const modalDiv = document.createElement("div");
    modalDiv.id = modalId;
    modalDiv.tabindex = "0";
    modalDiv.className = "fsa-modal";
    modalDiv.role = "dialog";
    modalDiv.ariaHidden = "true";
    const networkName = getData.getRefData()?.networks[stationMeta.networkCode?.toUpperCase()]?.name || "Unknown";
    const stateName = getData.getRefData()?.states[stationMeta.stateCode?.toUpperCase()]?.name || "Unknown";
    const modalHTML = /*html*/ `
        <div class="fsa-modal__dialog">
            <div class="fsa-modal__content">
                <button id="${closeModalBtnId}" class="fsa-modal__close" data-behavior="closes-modal" title="Close" aria-label="Close" type="button"></button>
                <h1 class="fsa-modal__title">${stationMeta.name}</h1>
                <div class="fsa-level fsa-level--justify-between">
                    <div class="fsa-level__item--align-middle">
                        <ul class="fsa-list--unstyled">
                            <li>Station Triplet: ${stationMeta.stationTriplet || "Unknown"}</li>
                            <li>Network: ${networkName}</li>
                            <li>State: ${stateName}</li>
                            <li>County: ${stationMeta.countyName || "Unknown"}</li>
                            <li>Elevation: ${stationMeta.elevation || "Unknown"} ft.</li>
                            <li>Latitide: ${stationMeta.latitude || "Unknown"}</li>
                            <li>Longitude: ${stationMeta.longitude || "Unknown"}</li>
                            <li>Reporting Since: ${stationBeginDate || "Unknown"}</li>
                            <li><a target="_blank" href="${imapLink}">See spatial context on the Interactive Map</a></li>
                            <li><a target="_blank" href="${rgLink}">Make a complex query with the Report Generator</a></li>
                        </ul>
                    </div>
                    <div class="fsa-level__item--align-middle">
                        <div id="${sitePhotoDivId}" class="fsa-box fsa-box--br-2 fsa-p--xs fsa-show@l">
                            <img 
                                class="fsa-level__item--align-middle" 
                                src="${photoLink}" 
                                alt="Photo of ${stationMeta.name} station" 
                                onerror='document.getElementById("${sitePhotoDivId}").className = "fsa-hide";'
                                width=250 
                                height=250
                            >
                        </div>
                    </div>
                </div>
            </div>
        </div>`;

    modalDiv.innerHTML = modalHTML;
    const showModalBtn = document.createElement("button");
    showModalBtn.id = showModalBtnId;
    showModalBtn.className = "fsa-btn fsa-btn--primary";
    showModalBtn.type = "button";
    showModalBtn.innerHTML = "Site Info";
    modal.appendChild(modalDiv);
    modal.appendChild(showModalBtn);
    attachTo.appendChild(modal);
    addModalListeners(modalId);
}

export function getElementsMetadataTable(elements) {
    const useCols = ["code", "name", "description"];
    const elementLookup = getData.getRefData().elements;
    const elementsTable = document.createElement("table");
    elementsTable.className =
        "fsa-table fsa-table--borderless fsa-table--valign-bottom fsa-table--responsive fsa-table--responsive-horizontal";
    const caption = document.createElement("caption");
    caption.innerHTML = "Element metadata/descriptions for selected element or report";
    caption.className = "sr-only";
    elementsTable.appendChild(caption);
    const tBody = document.createElement("tbody");
    elementsTable.appendChild(tBody);
    const headTr = document.createElement("tr");
    const tHead = document.createElement("thead");
    const tableData = [];
    elements.forEach((element) => {
        const elementMeta = elementLookup[element];
        const data = Object.fromEntries(Object.entries(elementMeta).filter(([key]) => useCols.includes(key)));
        tableData.push(data);
    });
    const columns = [];
    tableData.forEach((row) => {
        Object.keys(row).forEach((rowKey) => {
            if (!columns.includes(rowKey)) {
                columns.push(rowKey);
            }
        });
    });
    columns.forEach((col) => {
        const headTh = document.createElement("th");
        headTh.className = "fsa-text-align--left fsa-table__th--sticky";
        headTh.scope = "col";
        headTh.innerHTML = splitCamel(col);
        headTr.appendChild(headTh);
    });
    tHead.appendChild(headTr);
    elementsTable.appendChild(tHead);
    tableData.forEach((rowData) => {
        const tr = document.createElement("tr");
        columns.forEach((col) => {
            const td = document.createElement("td");
            const data = rowData[col] ? rowData[col] : "--";
            td.ariaLabel = splitCamel(col);
            td.className = "fsa-text-align--left";
            td.innerHTML = data;
            tr.appendChild(td);
        });
        tBody.appendChild(tr);
    });
    elementsTable.appendChild(tBody);
    return elementsTable;
}

export function attachElementMetaModalButton(attachTo, modalId = "modal--elements-meta") {
    const modal = document.createElement("div");
    const showModalBtnId = `btn-show-${modalId}`;
    const closeModalBtnId = `btn-close-${modalId}`;
    const modalDiv = document.createElement("div");
    modalDiv.id = modalId;
    modalDiv.tabindex = "0";
    modalDiv.className = "fsa-modal";
    modalDiv.role = "dialog";
    modalDiv.ariaHidden = "true";
    const elements = document.getElementById("select--element").selectedOptions[0].value.split(",");
    const elementsTitle =
        elements.length === 1
            ? elements.join("").toUpperCase()
            : document.getElementById("select--element").selectedOptions[0].innerHTML;
    const elementsTable = getElementsMetadataTable(elements);
    const modalHTML = /*html*/ `
        <div class="fsa-modal__dialog">
            <div class="fsa-modal__content">
                <button id="${closeModalBtnId}" class="fsa-modal__close" data-behavior="closes-modal" title="Close" aria-label="Close" type="button"></button>
                <h1 class="fsa-modal__title">${elementsTitle} Element${elements.length > 1 ? "s" : ""} Metadata</h1>
                <div class="fsa-level__item--align-middle">
                    ${elementsTable.outerHTML}
                </div>
            </div>
        </div>`;

    modalDiv.innerHTML = modalHTML;
    const showModalBtn = document.createElement("button");
    showModalBtn.id = showModalBtnId;
    showModalBtn.className = "fsa-btn fsa-btn--primary";
    showModalBtn.type = "button";
    showModalBtn.innerHTML = "Element Info";
    modal.appendChild(modalDiv);
    modal.appendChild(showModalBtn);
    attachTo.appendChild(modal);
    addModalListeners(modalId);
}
